import React from 'react';
import { DEFAULT_ROOMS } from '../mock';

// Coordinates measured against the new 1333×860 parchment map (Msg #869).
const LOCATIONS = {
  Sky_City:        { x: 49.0, y: 12.5, w: 130, h: 30 },
  Far_Planes:      { x: 15.0, y: 18.5, w: 150, h: 32 },
  Nexus_City:      { x: 82.0, y: 20.5, w: 150, h: 32 },
  WestTower:       { x: 27.0, y: 31.5, w: 130, h: 28 },
  EastTower:       { x: 72.0, y: 31.5, w: 130, h: 28 },
  Mandala:         { x: 49.5, y: 39.5, w: 140, h: 32 },
  Mountains:       { x: 12.5, y: 42.5, w: 120, h: 28 },
  Cafe:            { x: 36.0, y: 49.5, w: 100, h: 28 },
  Garden:          { x: 64.0, y: 45.7, w: 110, h: 28 },
  MainHall:        { x: 49.5, y: 50.0, w: 140, h: 32 },
  Desert:          { x: 85.5, y: 49.5, w: 100, h: 28 },
  OOC_Room:        { x: 40.0, y: 57.0, w: 130, h: 32 },
  Pub:             { x: 58.0, y: 57.0, w: 90,  h: 28 },
  Volcanic_Rim:    { x: 87.5, y: 59.0, w: 140, h: 32 },
  Oceana:          { x: 10.0, y: 60.0, w: 110, h: 30 },
  The_Barn:        { x: 28.0, y: 64.0, w: 110, h: 28 },
  Arena:           { x: 69.0, y: 70.5, w: 100, h: 28 },
  Forest:          { x: 19.0, y: 71.0, w: 100, h: 28 },
  Open_Seas:       { x:  8.0, y: 76.0, w: 120, h: 28 },
  Adventure_Quest: { x: 49.5, y: 78.0, w: 165, h: 32 },
  Cemetery:        { x: 85.0, y: 77.0, w: 105, h: 28 },
  Red_Dragon_Inn:  { x: 66.0, y: 84.0, w: 160, h: 32 },
  Church:          { x: 84.0, y: 84.0, w:  90, h: 28 },
  Night_Club:      { x: 39.0, y: 87.0, w: 120, h: 32 },
  Inn_Brothel:     { x: 46.0, y: 89.0, w: 130, h: 32 },
};

const WORLD_MAP_IMAGE =
  'https://customer-assets.emergentagent.com/job_rpg-chatroom/artifacts/jbq67e1g_World_Map.jpg';

// Msg #892 — mini-legend metadata for the hover tooltip
const ROOM_TAGS = {
  Sky_City:        { icon: '☁',  tag: 'Divine skyward city' },
  Far_Planes:      { icon: '✦',  tag: 'Otherworldly between-realm' },
  Nexus_City:      { icon: '⚙',  tag: 'Cosmopolitan crossroads' },
  WestTower:       { icon: '🜁',  tag: 'Mages\' tower' },
  EastTower:       { icon: '📖', tag: 'Scholars\' tower' },
  Mandala:         { icon: '🕉',  tag: 'Center of the world' },
  Mountains:       { icon: '⛰',  tag: 'Snow-veined peaks' },
  Cafe:            { icon: '☕', tag: 'Cozy chat' },
  Garden:          { icon: '🌸', tag: 'Perfumed garden' },
  MainHall:        { icon: '🏛',  tag: 'Great hall — every road starts here' },
  Desert:          { icon: '🌵', tag: 'Endless red dunes' },
  OOC_Room:        { icon: '💬', tag: 'Out-of-character parlour' },
  Pub:             { icon: '🍺', tag: 'Rowdy tavern' },
  Volcanic_Rim:    { icon: '🌋', tag: 'Molten monster hotspot' },
  Oceana:          { icon: '🌊', tag: 'Silver-sand bay' },
  The_Barn:        { icon: '🐴', tag: 'Warm timber barn' },
  Arena:           { icon: '⚔',  tag: 'PvP arena — combat scenes' },
  Forest:          { icon: '🌲', tag: 'Old-magic forest' },
  Open_Seas:       { icon: '⛵', tag: 'Stormy high-sea' },
  Adventure_Quest: { icon: '🗺',  tag: 'Adventure Quest hub' },
  Cemetery:        { icon: '⚰',  tag: 'Undead haunt' },
  Red_Dragon_Inn:  { icon: '🐉', tag: 'Legendary inn' },
  Church:          { icon: '⛪', tag: 'Silent sanctuary' },
  Night_Club:      { icon: '🎶', tag: 'Sultry night club' },
  Inn_Brothel:     { icon: '🌹', tag: 'Intimate boudoir-inn' },
  Strip_Club:      { icon: '💃', tag: 'Adults-only lounge' },
};

// Local set of monster names — populated from /npc_locations response so we
// don't have to hardcode the roster on the client.
const buildMonsterSet = (list) => new Set((list || []).map((m) => String(m).toLowerCase()));

const isMonsterName = (name, monsterSet) => {
  const base = String(name).replace(/_?\d+$/, '').replace(/_+$/, '').toLowerCase();
  return monsterSet.has(String(name).toLowerCase()) || monsterSet.has(base);
};

export const WorldMapModal = ({
  open, currentRoom, onJoin, onClose, apiBase, userId, roomUserCounts, onOpenProfile,
}) => {
  const [hoverRoom, setHoverRoom] = React.useState(null);
  const [liveCounts, setLiveCounts] = React.useState(roomUserCounts || {});
  // usersByRoom: { room: [{name, color}] }
  const [usersByRoom, setUsersByRoom] = React.useState({});
  // npcsByRoom: { room: [{name, kind:'npc'|'monster'}] }
  const [npcsByRoom, setNpcsByRoom] = React.useState({});

  // Msg #785 — fetch live presence AND NPC locations every 10s while open.
  React.useEffect(() => {
    if (!open || !apiBase) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const [pRes, nRes] = await Promise.all([
          fetch(`${apiBase}/presence`),
          fetch(`${apiBase}/npc_locations${userId ? `?user_id=${encodeURIComponent(userId)}` : ''}`),
        ]);
        if (!pRes.ok || !nRes.ok) return;
        const [pd, nd] = await Promise.all([pRes.json(), nRes.json()]);
        const monsterSet = buildMonsterSet(nd.monsters || []);
        const counts = {};
        const uMap = {};
        (pd.users || []).forEach((u) => {
          const room = u.room;
          if (!room || String(room).startsWith('private_')) return;
          counts[room] = (counts[room] || 0) + 1;
          (uMap[room] = uMap[room] || []).push({
            name: u.name, color: u.color || '#1a4a10',
          });
        });
        const nMap = {};
        Object.entries(nd.locations || {}).forEach(([name, where]) => {
          if (!where || where === 'offline') return;
          if (String(where).startsWith('private_')) return;
          (nMap[where] = nMap[where] || []).push({
            name, kind: isMonsterName(name, monsterSet) ? 'monster' : 'npc',
          });
        });
        if (!cancelled) {
          setLiveCounts(counts);
          setUsersByRoom(uMap);
          setNpcsByRoom(nMap);
        }
      } catch { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 10000);
    return () => { cancelled = true; clearInterval(id); };
  }, [open, apiBase, userId]);

  if (!open) return null;

  const openCharProfile = (name, kind, color) => {
    if (!onOpenProfile) return;
    if (kind === 'npc' || kind === 'monster') {
      onOpenProfile({ name, isNPC: true, isMonster: kind === 'monster' });
    } else {
      onOpenProfile({ name, color });
    }
  };

  return (
    <div
      data-testid="world-map-overlay"
      style={{
        position: 'fixed', inset: 0, zIndex: 50,
        background: 'rgba(8,12,8,0.75)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="world-map-modal"
        style={{
          width: 'min(98vw, 1300px)',
          height: 'min(96vh, 960px)',
          background: '#e9dab0',
          border: '3px double #5a4a1d',
          boxShadow: '0 12px 30px rgba(0,0,0,0.6)',
          fontFamily: 'Georgia, "Times New Roman", serif',
          color: '#3a2b14',
          padding: 14,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, flex: '0 0 auto' }}>
          <div style={{ fontSize: 22, fontWeight: 'bold', letterSpacing: 2, textTransform: 'uppercase' }}>
            ⚜ The Realms of Mandala ⚜
          </div>
          <button
            data-testid="world-map-close"
            onClick={onClose}
            style={{
              background: '#5a4a1d', color: '#f5e6c0',
              border: '1px solid #3a2b14', padding: '4px 14px',
              cursor: 'pointer', fontWeight: 'bold',
            }}
          >Close</button>
        </div>
        <div style={{ fontSize: 12, fontStyle: 'italic', marginBottom: 8, flex: '0 0 auto' }}>
          Click any location to travel there. Hover to see who&apos;s inside. Small pins indicate live occupants.
        </div>

        {/* Msg #798 — Scrollable map container so the parchment can be viewed at
            full size on smaller screens without cropping. */}
        <div
          data-testid="world-map-scroll"
          style={{
            flex: '1 1 auto',
            overflow: 'auto',
            border: '2px solid #6b5223',
            background: '#1a1108',
          }}
        >
          <div
            style={{
              position: 'relative',
              width: '100%',
              minWidth: 900,
              aspectRatio: '1333 / 860',
              backgroundImage: `url(${WORLD_MAP_IMAGE})`,
              backgroundSize: '100% 100%',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              boxShadow: 'inset 0 0 60px rgba(70,50,20,0.35)',
            }}
          >
          {DEFAULT_ROOMS.map((r) => {
            const loc = LOCATIONS[r];
            if (!loc) return null;
            const isCurrent = r === currentRoom;
            const uCount = (usersByRoom[r] || []).length;
            const nCount = (npcsByRoom[r] || []).filter((n) => n.kind === 'npc').length;
            const mCount = (npcsByRoom[r] || []).filter((n) => n.kind === 'monster').length;
            const hasAny = uCount + nCount + mCount > 0;
            return (
              <React.Fragment key={r}>
                <button
                  data-testid={`map-room-${r}`}
                  onClick={() => onJoin && onJoin(r)}
                  title={`Travel to ${r.replace(/_/g, ' ')}`}
                  style={{
                    position: 'absolute',
                    left: `${loc.x}%`,
                    top:  `${loc.y}%`,
                    width: loc.w ? `${loc.w}px` : 'auto',
                    height: loc.h ? `${loc.h}px` : 'auto',
                    transform: 'translate(-50%, -50%)',
                    background: isCurrent ? 'rgba(122,40,40,0.85)' : 'rgba(0,0,0,0)',
                    color: isCurrent ? '#fff3d0' : 'transparent',
                    border: isCurrent ? '2px solid #7a2828' : '2px dashed rgba(90,60,20,0)',
                    borderRadius: 6,
                    padding: 0,
                    fontFamily: 'Georgia, serif',
                    fontSize: 11,
                    fontWeight: 'bold',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    boxShadow: isCurrent ? '0 2px 6px rgba(60,40,20,0.6)' : 'none',
                    transition: 'background 120ms, color 120ms, border-color 120ms',
                  }}
                  onMouseEnter={(e) => {
                    setHoverRoom(r);
                    if (isCurrent) return;
                    e.currentTarget.style.background = 'rgba(255,235,180,0.55)';
                    e.currentTarget.style.color = '#3a2b14';
                    e.currentTarget.style.borderColor = 'rgba(90,60,20,0.65)';
                  }}
                  onMouseLeave={(e) => {
                    setHoverRoom((cur) => (cur === r ? null : cur));
                    if (isCurrent) return;
                    e.currentTarget.style.background = 'rgba(0,0,0,0)';
                    e.currentTarget.style.color = 'transparent';
                    e.currentTarget.style.borderColor = 'rgba(90,60,20,0)';
                  }}
                >
                  {isCurrent ? `${r.replace(/_/g, ' ')} (you)` : ''}
                </button>

                {/* Live-occupancy pins — small colored dots stacked next to the room */}
                {hasAny && (
                  <div
                    data-testid={`map-pins-${r}`}
                    style={{
                      position: 'absolute',
                      left: `${loc.x}%`,
                      top: `calc(${loc.y}% + ${(loc.h || 28) / 2 + 4}px)`,
                      transform: 'translate(-50%, 0)',
                      display: 'flex',
                      gap: 3,
                      pointerEvents: 'none',
                      zIndex: 3,
                    }}
                  >
                    {uCount > 0 && (
                      <span
                        data-testid={`map-pin-user-${r}`}
                        title={`${uCount} user${uCount > 1 ? 's' : ''}`}
                        style={{
                          background: '#1c3a1c', color: '#ecfcd9',
                          padding: '1px 6px', borderRadius: 8,
                          fontSize: 10, fontWeight: 'bold',
                          border: '1px solid #ecfcd9',
                        }}
                      >👤 {uCount}</span>
                    )}
                    {nCount > 0 && (
                      <span
                        data-testid={`map-pin-npc-${r}`}
                        title={`${nCount} NPC${nCount > 1 ? 's' : ''}`}
                        style={{
                          background: '#6b3a92', color: '#f5e6c0',
                          padding: '1px 6px', borderRadius: 8,
                          fontSize: 10, fontWeight: 'bold',
                          border: '1px solid #f5e6c0',
                        }}
                      >★ {nCount}</span>
                    )}
                    {mCount > 0 && (
                      <span
                        data-testid={`map-pin-monster-${r}`}
                        title={`${mCount} monster${mCount > 1 ? 's' : ''}`}
                        style={{
                          background: '#8b1a1a', color: '#fff3d0',
                          padding: '1px 6px', borderRadius: 8,
                          fontSize: 10, fontWeight: 'bold',
                          border: '1px solid #fff3d0',
                        }}
                      >☠ {mCount}</span>
                    )}
                  </div>
                )}
              </React.Fragment>
            );
          })}
          </div>
        </div>

        {/* Msg #798 — Legend has a RESERVED min-height so hovering different
            rooms does NOT reflow the modal (which is what caused the
            perceived "shake"). */}
        <div
          data-testid={hoverRoom ? `map-legend-${hoverRoom}` : 'map-legend-empty'}
          style={{
            marginTop: 10,
            background: hoverRoom ? '#f5eec8' : 'transparent',
            border: hoverRoom ? '1px solid #6b5223' : '1px solid transparent',
            padding: '8px 12px',
            fontSize: 12,
            color: '#3a2b14',
            minHeight: 96,
            flex: '0 0 auto',
          }}
        >
          {hoverRoom ? (
            <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <span style={{ fontSize: 18 }}>{ROOM_TAGS[hoverRoom]?.icon || '📍'}</span>
              <b style={{ minWidth: 130 }}>{hoverRoom.replace(/_/g, ' ')}</b>
              <span style={{ flex: 1, fontStyle: 'italic' }}>
                {ROOM_TAGS[hoverRoom]?.tag || 'Location of Mandala'}
              </span>
              <span
                data-testid={`map-legend-count-${hoverRoom}`}
                style={{
                  background: '#1c3a1c', color: '#ecfcd9',
                  padding: '2px 8px', borderRadius: 8, fontWeight: 'bold',
                }}
              >
                👥 {liveCounts[hoverRoom] || 0} online
              </span>
            </div>

            {/* Occupant lists */}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {(usersByRoom[hoverRoom] || []).length > 0 && (
                <div data-testid={`map-legend-users-${hoverRoom}`} style={{ flex: '1 1 200px' }}>
                  <div style={{ fontWeight: 'bold', fontSize: 11, marginBottom: 2 }}>👤 Users</div>
                    {(usersByRoom[hoverRoom] || []).map((u, i) => (
                    <span
                      key={`u-${hoverRoom}-${u.name}-${i}`}
                      data-testid={`map-legend-user-${u.name}`}
                      onClick={() => openCharProfile(u.name, 'user', u.color)}
                      style={{
                        display: 'inline-block',
                        color: u.color || '#1a4a10',
                        fontWeight: 'bold',
                        marginRight: 8,
                        cursor: 'pointer',
                        textDecoration: 'underline dotted',
                      }}
                    >
                      {u.name}
                    </span>
                  ))}
                </div>
              )}
              {(npcsByRoom[hoverRoom] || []).filter((n) => n.kind === 'npc').length > 0 && (
                <div data-testid={`map-legend-npcs-${hoverRoom}`} style={{ flex: '1 1 200px' }}>
                  <div style={{ fontWeight: 'bold', fontSize: 11, marginBottom: 2 }}>★ NPCs</div>
                    {(npcsByRoom[hoverRoom] || [])
                    .filter((n) => n.kind === 'npc')
                    .map((n, i) => (
                      <span
                        key={`n-${hoverRoom}-${n.name}-${i}`}
                        data-testid={`map-legend-npc-${n.name}`}
                        onClick={() => openCharProfile(n.name, 'npc')}
                        style={{
                          display: 'inline-block',
                          color: '#6b3a92',
                          fontWeight: 'bold',
                          marginRight: 8,
                          cursor: 'pointer',
                          textDecoration: 'underline dotted',
                        }}
                      >
                        {n.name.replace(/_/g, ' ')}
                      </span>
                  ))}
                </div>
              )}
              {(npcsByRoom[hoverRoom] || []).filter((n) => n.kind === 'monster').length > 0 && (
                <div data-testid={`map-legend-monsters-${hoverRoom}`} style={{ flex: '1 1 200px' }}>
                  <div style={{ fontWeight: 'bold', fontSize: 11, marginBottom: 2 }}>☠ Monsters</div>
                    {(npcsByRoom[hoverRoom] || [])
                    .filter((n) => n.kind === 'monster')
                    .map((n, i) => (
                      <span
                        key={`m-${hoverRoom}-${n.name}-${i}`}
                        data-testid={`map-legend-monster-${n.name}`}
                        onClick={() => openCharProfile(n.name, 'monster')}
                        style={{
                          display: 'inline-block',
                          color: '#8b1a1a',
                          fontWeight: 'bold',
                          marginRight: 8,
                          cursor: 'pointer',
                          textDecoration: 'underline dotted',
                        }}
                      >
                        {n.name.replace(/_/g, ' ')}
                      </span>
                  ))}
                </div>
              )}
              {(usersByRoom[hoverRoom] || []).length === 0 &&
                (npcsByRoom[hoverRoom] || []).length === 0 && (
                  <div style={{ fontStyle: 'italic', color: '#6b5223' }}>No one currently here.</div>
                )}
            </div>
            </>
          ) : (
            <div style={{ fontStyle: 'italic', color: '#6b5223', textAlign: 'center', paddingTop: 24 }}>
              Hover any location above to see who&apos;s inside.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorldMapModal;
